import { useGoogleRecaptchaV2 } from '@sgcmpteltd/uikit-next';
import {
  AutoResizeTextArea,
  ErrorMessage,
  Input,
} from '@sgcmpteltd/uikit-react';
import {
  Button,
  BUTTON_COLOR_RED,
  BUTTON_VARIANT_CONTAINED,
} from '@sgcmpteltd/uikit-react/components/button';
import { useRef, useState, useTransition } from 'react';

import { GOOGLE_RECAPTCHA_SITE_KEY } from '@/configs/google/recaptcha';
import { useValidation } from '@/hooks/use-validation';

import { RecaptchaBrandingText } from '../../recaptcha-branding-text';
import type { OnGoogleRecaptchSuccess } from '../types';
import styles from './styles.module.scss';
import type { Component } from './types';
import { reportErrorDataSchema } from './validation';

export const Step: Component = ({
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [isRecaptchaPending, setIsRecaptchaPending] = useState(false);

  const [isReportErrorPending, startReportErrorTransition] = useTransition();

  const googleRecaptchaWidgetElRef = useRef<HTMLDivElement | null>(null);
  const onGoogleRecaptchSuccessRef = useRef<
    OnGoogleRecaptchSuccess | null
  >(null);

  const {
    errorMessageMap,
    validate,
  } = useValidation({
    schema: reportErrorDataSchema,
  });

  const {
    reset: resetRecaptcha,
    execute: executeRecaptcha,
  } = useGoogleRecaptchaV2({
    siteKey: GOOGLE_RECAPTCHA_SITE_KEY ?? '',
    widgetElRef: googleRecaptchaWidgetElRef,
    optionsRef: useRef({
      size: 'invisible',
    }),
    onSuccess: () => {
      setIsRecaptchaPending(false);

      onGoogleRecaptchSuccessRef.current?.();
    },
    onExpired: () => {
      setIsRecaptchaPending(false);
    },
    onError: () => {
      setIsRecaptchaPending(false);
    },
  });

  return (
    <div
      className={styles.containerRoot}>
      <span
        className={styles.textStepTitle}>
        Report Error
      </span>
      <div
        className={styles.containerFormFieldList}>
        <Input
          isInvalid={!!errorMessageMap?.name}
          placeholder="Name*"
          value={name}
          onChange={({
            target: {
              value: newName,
            },
          }) => {
            setName(newName);
          }} />
        <Input
          isInvalid={!!errorMessageMap?.email}
          placeholder="Email Address*"
          value={email}
          onChange={({
            target: {
              value: newEmail,
            },
          }) => {
            setEmail(newEmail);
          }} />
        <div
          className={styles.containerFormField}>
          <AutoResizeTextArea
            isInvalid={!!errorMessageMap?.description}
            placeholder="Enter a Description*"
            rows={14}
            value={description}
            onChange={({
              target: {
                value: newDescription,
              },
            }) => {
              setDescription(newDescription);
            }} />
          <ErrorMessage
            className={styles.textFieldError}>
            *Compulsory Field
          </ErrorMessage>
        </div>
        <div
          ref={googleRecaptchaWidgetElRef} />
      </div>
      <div
        className={styles.containerFooter}>
        <div
          className={styles.containerSubmitButton}>
          <Button
            color={BUTTON_COLOR_RED}
            disabled={isRecaptchaPending || isReportErrorPending}
            variant={BUTTON_VARIANT_CONTAINED}
            onClick={() => {
              if (!onSubmit) {
                return;
              }

              const { success, output } = validate({
                description,
                email,
                name,
              });

              if (!success) {
                return;
              }

              onGoogleRecaptchSuccessRef.current = () => {
                if (!onSubmit) {
                  return;
                }

                const {
                  description: outputDescription,
                  email: outputEmail,
                  name: outputName,
                } = output;

                startReportErrorTransition(async () => {
                  await onSubmit({
                    description: outputDescription,
                    email: outputEmail,
                    name: outputName,
                  });
                });
              };

              resetRecaptcha();
              executeRecaptcha();
            }}>
            Submit
          </Button>
        </div>
        <div
          className={styles.containerRecaptchaBrandingText}>
          <RecaptchaBrandingText />
        </div>
      </div>
    </div>
  );
};
